import type { Plan, PlanDay, Ridden, Suggestion } from '@painpal/contracts';
import { ChevronRight, Plus } from 'lucide-react';
import type { Ref } from 'react';
import { formatDayMonth, formatHoursMinutes, formatWhole, weekdayName } from '../../lib/format';
import { suggestedRowLabel, suggestionDuration, whyLine } from '../recommendations/recommendationModel';
import { PlanTag } from './PlanTag';
import { formatEstimate, planDuration, rowLabel } from './planModel';
import styles from './calendar.module.css';

function DayCell({ date, today }: { date: string; today: boolean }) {
  return (
    <span className={styles.day}>
      <span className={styles.dayTop}>
        <span className={styles.weekday}>{weekdayName(date)}</span>
        {today && <span className={styles.todayMark}>TODAY</span>}
      </span>
      <span className={styles.date}>{formatDayMonth(date)}</span>
    </span>
  );
}

function PlanCells({ plan, missed }: { plan: Plan; missed: boolean }) {
  const duration = planDuration(plan);
  return (
    <>
      <span className={styles.planCell}>
        <PlanTag type={plan.type} missed={missed} />
        {duration !== null && (
          <span className={styles.duration} data-defaulted={duration.defaulted || undefined}>
            {duration.text}{duration.defaulted && <span className={styles.defaultMark}>DEFAULT</span>}
          </span>
        )}
      </span>
      <span className={styles.note} data-empty={plan.note === null || undefined}>{plan.note ?? '—'}</span>
      <span className={styles.estimate} data-zero={plan.estimatedLoad === 0 || undefined}>
        <span className={styles.figure}>{formatEstimate(plan.estimatedLoad)}</span><span className={styles.unit}>LOAD</span>
      </span>
    </>
  );
}

function RiddenCell({ ridden, status }: { ridden: Ridden; status: PlanDay['status'] }) {
  if (ridden.movingS === 0) return <span className={styles.ridden} data-empty="true">—</span>;
  return (
    <span className={styles.ridden}>
      {status === 'done' && <span className={styles.doneDot} aria-hidden="true" />}
      <span className={styles.riddenWord}>RIDDEN</span>
      <span className={styles.figure}>{formatHoursMinutes(ridden.movingS)}</span> · <span className={styles.figure}>{formatWhole(ridden.load)}</span>
      <span className={styles.unit}>LOAD</span>
    </span>
  );
}

// Boards th54u / lycWS: outline tag and grey figures. The why line is desktop only (CSS); on phone the day editor shows it.
function SuggestedCells({ suggestion }: { suggestion: Suggestion }) {
  return (
    <>
      <span className={styles.planCell}>
        <PlanTag type={suggestion.type} outline />
        <span className={styles.duration} data-quiet="true">{suggestionDuration(suggestion)}</span>
      </span>
      <span className={styles.note} data-suggested="true">
        <span className={styles.structure}>{suggestion.structure}</span>
        <span className={styles.why}>{whyLine(suggestion.reasons)}</span>
      </span>
      <span className={styles.estimate} data-zero="true">
        <span className={styles.figure}>{formatEstimate(suggestion.estimatedLoad)}</span><span className={styles.unit}>LOAD</span>
      </span>
    </>
  );
}

function DayCells({ day, suggestion }: { day: PlanDay; suggestion: Suggestion | null }) {
  const { plan, ridden, status } = day;
  let body = <span className={styles.unplanned}>—</span>;
  if (plan !== null) body = <PlanCells plan={plan} missed={status === 'missed'} />;
  else if (suggestion !== null) body = <SuggestedCells suggestion={suggestion} />;
  let side = <RiddenCell ridden={ridden} status={status} />;
  if (plan === null && ridden.movingS === 0) {
    side = suggestion === null ? <span className={styles.addLabel}>ADD BLOCK</span> : <span className={styles.suggestedLabel}>SUGGESTED</span>;
  }
  return <>{body}{side}</>;
}

export interface CalendarRowProps {
  day: PlanDay; today: string; expanded: boolean; onEdit: () => void; suggestion?: Suggestion | null; ref?: Ref<HTMLButtonElement>;
}

// A suggested row leaves its edit cell empty: the + that adds it is a sibling button, as buttons can't nest.
export function CalendarRow({ day, today, expanded, onEdit, suggestion = null, ref }: CalendarRowProps) {
  const isToday = day.date === today;
  const shown = day.plan === null ? suggestion : null;
  return (
    <button
      ref={ref} type="button" className={styles.row} aria-haspopup="dialog" aria-expanded={expanded} onClick={onEdit}
      aria-label={`Edit ${shown === null ? rowLabel(day) : suggestedRowLabel(day.date, shown)}`}
      data-today={isToday || undefined} data-planned={day.plan !== null} data-suggested={shown !== null || undefined}
    >
      <DayCell date={day.date} today={isToday} />
      <DayCells day={day} suggestion={shown} />
      <span className={styles.edit} aria-hidden="true">{day.plan !== null ? <ChevronRight size={16} /> : shown === null && <Plus size={16} />}</span>
    </button>
  );
}
